import { Form } from "antd";
import { useGetOrderVatInfo, useUpdateOrderVatInfo } from "hooks/order";
import { t } from "i18next";
import { pick, uniq } from "lodash";
import { useEffect, useState } from "react";
import { notify } from "utils/helperFuncs";

/** Số chip gợi ý tối đa cho mỗi field, theo yêu cầu BA */
export const MAX_VAT_SUGGESTIONS = 3;

/**
 * Tên 4 field VAT. Đúng một chỗ khai báo cho cả 3 nơi phải trùng khớp nhau: `name` của Form.Item,
 * field của `OrderVatInfo` (query gợi ý) và key của `UpdateOrderVatInfoInput` (mutation) — gõ lệch
 * một ký tự là chip gắn sai ô hoặc BE nhận thiếu field, mà không có lỗi nào nổ ra.
 */
export const VAT_FIELD = {
  TAX_ID_NUMBER: "vatTaxIDNumber",
  COMPANY_NAME: "vatCompanyName",
  EMAIL: "vatEmail",
  ADDRESS: "vatAddress",
};

export const VAT_FIELDS = Object.values(VAT_FIELD);

/** MST 10 số của đơn vị chủ quản; 3 số sau dấu gạch là thứ tự đơn vị phụ thuộc */
const TAX_CODE_PREFIX_LENGTH = 10;
const MAX_TAX_CODE_DIGITS = 13;

/**
 * State + submit cho pop up "Thông tin xuất hoá đơn VAT". KHÔNG đụng vào dữ liệu đơn cho tới khi
 * bấm [Lưu lại] — [Huỷ bỏ] phải trả lại nguyên dữ liệu ban đầu của đơn.
 */
const useModalUpdateVatInfo = ({ order, visible, onClose, onRefetch }) => {
  const [form] = Form.useForm();
  const [formValues, setFormValues] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const { handleUpdateOrderVatInfo } = useUpdateOrderVatInfo();
  const { vatInfo, vatSuggestions, loading } = useGetOrderVatInfo({ orderID: order?.id, skip: !visible });

  // Nạp lại giá trị mỗi lần mở pop up: mở lại sau khi Huỷ phải thấy dữ liệu gốc, không phải phần
  // nhập dở lần trước. Trong lúc chờ API thì seed bằng dữ liệu đơn có sẵn ở màn chi tiết — cũng là
  // đường lùi khi query lỗi; `current` của API về mới là nguồn đúng và ghi đè lên.
  useEffect(() => {
    if (!visible) return;

    const values = pick(vatInfo ?? order ?? {}, VAT_FIELDS);
    form.setFieldsValue(values);
    setFormValues(values);
    // Deps CHỈ gồm 3 thứ định danh "lần mở pop up này" + lô dữ liệu API. Đừng thêm `order` —
    // `onRefetch` ở nhánh catch cập nhật `order` khi pop up còn mở, resync sẽ xoá giá trị đang nhập.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, order?.id, vatInfo]);

  /**
   * Chuẩn hoá MST: bỏ hết ký tự không phải chữ số, rồi tự chèn dấu gạch sau 10 số. MST 13 số của
   * đơn vị phụ thuộc là 10 số chủ quản + 3 số thứ tự, nên chữ số thứ 11 trở đi chỉ có thể là phần
   * sau dấu gạch — dán `0302525162001` cũng thành `0302525162-001`.
   */
  const normalizeTaxCode = (value) => {
    const digits = `${value ?? ""}`.replace(/\D/g, "").slice(0, MAX_TAX_CODE_DIGITS);
    return digits.length > TAX_CODE_PREFIX_LENGTH
      ? `${digits.slice(0, TAX_CODE_PREFIX_LENGTH)}-${digits.slice(TAX_CODE_PREFIX_LENGTH)}`
      : digits;
  };

  /**
   * Chip gợi ý cho 1 field. API trả cả bộ VAT (`[OrderVatInfo!]`) nên phải tách theo field: bỏ giá
   * trị rỗng, bỏ trùng nhau giữa các bộ, bỏ giá trị đang có ở ô nhập, tối đa 3 chip.
   */
  const getSuggestions = (name) =>
    uniq((vatSuggestions ?? []).map((item) => item?.[name]))
      .filter((value) => value && value !== formValues?.[name])
      .slice(0, MAX_VAT_SUGGESTIONS);

  const onSelectSuggestion = (name, value) => {
    form.setFields([{ name, value, errors: [] }]);
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      // Gửi đủ 4 field dù input của BE cho nullable — cả 4 đều bắt buộc theo yêu cầu nghiệp vụ,
      // không phụ thuộc cách BE xử lý `null`.
      await handleUpdateOrderVatInfo({ orderID: order?.id, ...values });

      notify.success({ message: t("v2.order.detail.modalUpdateVatInfo.updateSuccess") });
      onClose?.();
      // Mutation trả `Boolean!`, không trả đơn mới → bắt buộc refetch để khối VAT ở màn chi tiết
      // hiển thị giá trị vừa lưu.
      await onRefetch?.();
    } catch (error) {
      if (error?.errorFields) return; // lỗi validate của antd, đã hiện dưới field
      notify.error({
        message: t("v2.order.detail.modalUpdateVatInfo.updateError"),
        description: error?.message,
      });
      // Đơn đổi trạng thái trước khi lưu cũng về đây — refetch để icon [Chỉnh sửa] tự ẩn theo
      // trạng thái mới, tránh user bấm lưu lại lần nữa cũng lỗi.
      await onRefetch?.();
    } finally {
      setSubmitting(false);
    }
  };

  return {
    form,
    loading,
    submitting,
    setFormValues,
    normalizeTaxCode,
    getSuggestions,
    onSelectSuggestion,
    handleSave,
  };
};

export default useModalUpdateVatInfo;
